import { CONFIG } from "../engine/config";
import type { EchoEngine } from "../engine/engine";
import type { EngineEvent } from "../engine/events";
import type { EchoDef } from "../engine/config";
import type { GameStore } from "./store";
import type { CoreStyle } from "./catalog";

/**
 * EchoRenderer — canvas presentation layer.
 *
 * Neon technique (see docs/MARKET_RESEARCH.md §2-3): glow is pre-rendered
 * radial-gradient sprites blitted with `lighter` blending — never per-frame
 * shadowBlur, which is a mobile performance killer. Brightness hierarchy is
 * strict (spec §14.1): player > shard > echo heads > echo trails > history >
 * decoration.
 */

export interface Layout {
  W: number;
  H: number;
  s: number;
  ax: number;
  ay: number;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  col: string;
  size: number;
}

interface HitFx {
  echoId: string;
  delay: number;
  x: number;
  y: number;
  age: number;
}

export class RenderFx {
  particles: Particle[] = [];
  nudge = 0;
  nudgeAng = 0;
  hitFx: HitFx | null = null;
  pickupPop: { x: number; y: number; age: number } | null = null;
  unlockFlash: { echoId: string; age: number } | null = null;
  deathAge = -1; // ≥0 once the run is lost
  clearAge = -1;
  tetherFade = 0;
  floaters: { x: number; y: number; text: string; age: number }[] = [];
  lifeFlash: { index: number; age: number } | null = null;

  spawnBurst(x: number, y: number, col: string, reduced: boolean, vx = 0, vy = 0): void {
    const n = reduced ? 4 : 14;
    for (let i = 0; i < n; i++) {
      const a = Math.random() * 6.283;
      const sp = 40 + Math.random() * 130;
      this.particles.push({
        x,
        y,
        // particles inherit ~40 % of source velocity (Vlambeer)
        vx: Math.cos(a) * sp + vx * 0.4,
        vy: Math.sin(a) * sp + vy * 0.4,
        life: 0.35 + Math.random() * 0.4,
        max: 0.75,
        col,
        size: 1.4 + Math.random() * 1.1,
      });
    }
  }

  applyEvents(events: EngineEvent[], engine: EchoEngine, reduced: boolean): void {
    for (const e of events) {
      switch (e.type) {
        case "pickup": {
          this.spawnBurst(e.x, e.y, "#FFE37B", reduced);
          this.pickupPop = { x: e.x, y: e.y, age: 0 };
          if (!reduced) {
            this.floaters.push({ x: e.x, y: e.y, text: `+${e.points}`, age: 0 });
          }
          break;
        }
        case "hit": {
          this.spawnBurst(engine.px, engine.py, "#FF719A", reduced);
          this.hitFx = { echoId: e.echoId, delay: e.delay, x: e.x, y: e.y, age: 0 };
          this.nudge = 2;
          this.nudgeAng = Math.atan2(engine.py - e.y, engine.px - e.x);
          this.lifeFlash = { index: e.livesLeft, age: 0 };
          break;
        }
        case "unlock": {
          this.unlockFlash = { echoId: e.echoId, age: 0 };
          break;
        }
        case "ended": {
          if (e.win) this.clearAge = 0;
          else this.deathAge = 0;
          break;
        }
        default:
          break;
      }
    }
  }

  tick(dt: number, touching: boolean): void {
    if (this.nudge > 0) this.nudge = Math.max(0, this.nudge - dt * 22);
    if (this.hitFx) {
      this.hitFx.age += dt;
      if (this.hitFx.age > 0.8) this.hitFx = null;
    }
    if (this.pickupPop) {
      this.pickupPop.age += dt;
      if (this.pickupPop.age > 0.3) this.pickupPop = null;
    }
    if (this.unlockFlash) {
      this.unlockFlash.age += dt;
      if (this.unlockFlash.age > 0.6) this.unlockFlash = null;
    }
    if (this.deathAge >= 0) this.deathAge += dt;
    if (this.clearAge >= 0) this.clearAge += dt;
    this.tetherFade = touching ? 0.12 : Math.max(0, this.tetherFade - dt);
    if (this.lifeFlash) {
      this.lifeFlash.age += dt;
      if (this.lifeFlash.age > 0.45) this.lifeFlash = null;
    }
    for (let i = this.floaters.length - 1; i >= 0; i--) {
      this.floaters[i].age += dt;
      if (this.floaters[i].age > 0.55) this.floaters.splice(i, 1);
    }
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.life -= dt;
      if (p.life <= 0) {
        this.particles.splice(i, 1);
        continue;
      }
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vx *= 0.94;
      p.vy *= 0.94;
    }
  }

  reset(): void {
    this.particles = [];
    this.nudge = 0;
    this.hitFx = null;
    this.pickupPop = null;
    this.unlockFlash = null;
    this.deathAge = -1;
    this.clearAge = -1;
    this.floaters = [];
    this.lifeFlash = null;
  }
}

const GLOW_CACHE = new Map<string, HTMLCanvasElement>();

/** Pre-rendered radial glow sprite: white-hot core → color halo → transparent. */
function glowSprite(col: string): HTMLCanvasElement {
  let c = GLOW_CACHE.get(col);
  if (c) return c;
  c = document.createElement("canvas");
  c.width = 96;
  c.height = 96;
  const x = c.getContext("2d")!;
  const g = x.createRadialGradient(48, 48, 0, 48, 48, 48);
  g.addColorStop(0, "rgba(255,255,255,0.85)");
  g.addColorStop(0.18, hexA(col, 0.75));
  g.addColorStop(0.45, hexA(col, 0.22));
  g.addColorStop(1, hexA(col, 0));
  x.fillStyle = g;
  x.fillRect(0, 0, 96, 96);
  GLOW_CACHE.set(col, c);
  return c;
}

function hexA(hex: string, a: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${a})`;
}

export class EchoRenderer {
  private bg: HTMLCanvasElement | null = null;
  private bgKey = "";
  ambientT = 0;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly store: GameStore,
  ) {}

  layout(): Layout | null {
    const cv = this.canvas;
    const W = cv.clientWidth;
    const H = cv.clientHeight;
    if (W === 0 || H === 0) return null;
    const dpr = Math.min(devicePixelRatio || 1, 2);
    if (cv.width !== Math.round(W * dpr) || cv.height !== Math.round(H * dpr)) {
      cv.width = Math.round(W * dpr);
      cv.height = Math.round(H * dpr);
    }
    const ctx = cv.getContext("2d")!;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const top = 118;
    const bot = 92;
    const pad = 10;
    const s = Math.min((W - pad * 2) / CONFIG.arenaW, (H - top - bot) / CONFIG.arenaH);
    const ax = (W - CONFIG.arenaW * s) / 2;
    const ay = top + (H - top - bot - CONFIG.arenaH * s) / 2;
    return { W, H, s, ax, ay };
  }

  A(L: Layout, x: number, y: number): [number, number] {
    return [L.ax + x * L.s, L.ay + y * L.s];
  }

  private paletteCols(): string[] {
    return this.store.equippedItem("palette").cols;
  }

  private coreStyle(): CoreStyle {
    return this.store.equippedItem("core").data as unknown as CoreStyle;
  }

  private shapeSet(): string[] {
    return (this.store.equippedItem("shapes").data?.set as string[]) ?? [
      "circle",
      "diamond",
      "split",
      "hex",
    ];
  }

  private shapeFor(def: EchoDef, index: number): string {
    const set = this.shapeSet();
    if (set === undefined) return def.shape;
    return set[index % set.length] ?? def.shape;
  }

  // ---------- background ----------

  private drawBackground(ctx: CanvasRenderingContext2D, L: Layout): void {
    const arena = this.store.equippedItem("arena");
    const contrast = this.store.data.settings.contrast;
    const key = `${arena.id}|${L.W}x${L.H}|${L.s.toFixed(4)}|${contrast}`;
    if (this.bgKey !== key || !this.bg) {
      this.bgKey = key;
      const dpr = Math.min(devicePixelRatio || 1, 2);
      const bg = document.createElement("canvas");
      bg.width = Math.round(L.W * dpr);
      bg.height = Math.round(L.H * dpr);
      const b = bg.getContext("2d")!;
      b.setTransform(dpr, 0, 0, dpr, 0, 0);
      const d = arena.data as Record<string, string | boolean>;
      const grad = b.createLinearGradient(0, 0, 0, L.H);
      grad.addColorStop(0, d.top as string);
      grad.addColorStop(0.5, d.mid as string);
      grad.addColorStop(1, d.bot as string);
      b.fillStyle = grad;
      b.fillRect(0, 0, L.W, L.H);
      // starfield (Deep Field)
      if (d.stars) {
        b.fillStyle = "rgba(220,232,255,0.5)";
        let sx = 12.9898;
        for (let i = 0; i < 70; i++) {
          sx = (sx * 9301 + 49297) % 233280;
          const rx = sx / 233280;
          sx = (sx * 9301 + 49297) % 233280;
          const ry = sx / 233280;
          sx = (sx * 9301 + 49297) % 233280;
          const rr = sx / 233280;
          b.globalAlpha = 0.12 + rr * 0.3;
          b.fillRect(rx * L.W, ry * L.H, rr < 0.3 ? 2 : 1, rr < 0.3 ? 2 : 1);
        }
        b.globalAlpha = 1;
      }
      // arena grid
      let gridCol = d.grid as string;
      if (contrast) gridCol = "rgba(167,171,192,0.14)";
      b.strokeStyle = gridCol;
      b.lineWidth = 1;
      for (let gx = 0; gx <= CONFIG.arenaW; gx += 30) {
        const p1 = this.A(L, gx, 0);
        const p2 = this.A(L, gx, CONFIG.arenaH);
        b.beginPath();
        b.moveTo(p1[0], p1[1]);
        b.lineTo(p2[0], p2[1]);
        b.stroke();
      }
      for (let gy = 0; gy <= CONFIG.arenaH; gy += 30) {
        const p3 = this.A(L, 0, gy);
        const p4 = this.A(L, CONFIG.arenaW, gy);
        b.beginPath();
        b.moveTo(p3[0], p3[1]);
        b.lineTo(p4[0], p4[1]);
        b.stroke();
      }
      // rounded arena frame
      const tl = this.A(L, 0, 0);
      b.strokeStyle = contrast ? "rgba(167,171,192,0.4)" : "rgba(66,218,246,0.18)";
      b.lineWidth = 1.5;
      roundRect(b, tl[0] - 4, tl[1] - 4, CONFIG.arenaW * L.s + 8, CONFIG.arenaH * L.s + 8, 14);
      b.stroke();
      this.bg = bg;
    }
    ctx.drawImage(this.bg, 0, 0, L.W, L.H);
  }

  invalidateBackground(): void {
    this.bgKey = "";
  }

  // ---------- ambient (home / menus) ----------

  drawAmbient(ctx: CanvasRenderingContext2D, L: Layout): void {
    this.drawBackground(ctx, L);
    const colsStatic = this.paletteCols();
    if (this.store.data.settings.reduced) {
      // motionless constellation — reduced motion never means an empty room
      ctx.save();
      for (let i = 0; i < 4; i++) {
        const x = 180 + Math.sin(i * 1.9) * 130;
        const y = 280 + Math.cos(i * 2.4) * 200;
        const p = this.A(L, x, y);
        ctx.globalAlpha = 0.35;
        ctx.fillStyle = colsStatic[i % colsStatic.length];
        ctx.beginPath();
        ctx.arc(p[0], p[1], 4 * L.s, 0, 6.283);
        ctx.fill();
      }
      ctx.restore();
      return;
    }
    const cols = this.paletteCols();
    const t = this.ambientT;
    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    for (let i = 0; i < 4; i++) {
      ctx.beginPath();
      for (let k = 0; k < 20; k++) {
        const tt = t - k * 0.06;
        const xx = 180 + Math.sin(tt * 0.31 + i * 1.9) * 130;
        const yy = 280 + Math.cos(tt * 0.23 + i * 2.4) * 200;
        const pp = this.A(L, xx, yy);
        if (k === 0) ctx.moveTo(pp[0], pp[1]);
        else ctx.lineTo(pp[0], pp[1]);
      }
      ctx.strokeStyle = cols[i % cols.length];
      ctx.globalAlpha = 0.12;
      ctx.lineWidth = 2 * L.s;
      ctx.stroke();
      const x = 180 + Math.sin(t * 0.31 + i * 1.9) * 130;
      const y = 280 + Math.cos(t * 0.23 + i * 2.4) * 200;
      const p = this.A(L, x, y);
      ctx.globalAlpha = 0.55;
      const g = glowSprite(cols[i % cols.length]);
      const R = 16 * L.s;
      ctx.drawImage(g, p[0] - R, p[1] - R, R * 2, R * 2);
    }
    ctx.restore();
  }

  // ---------- run ----------

  drawRun(engine: EchoEngine, fx: RenderFx, readyT: number, trainingBanner: boolean): void {
    const L = this.layout();
    if (!L) return;
    const ctx = this.canvas.getContext("2d")!;
    const set = this.store.data.settings;
    const reduced = set.reduced;
    const contrast = set.contrast;
    const cols = this.paletteCols();
    const core = this.coreStyle();

    this.drawBackground(ctx, L);
    ctx.save();
    if (fx.nudge > 0 && set.nudge && !reduced) {
      ctx.translate(Math.cos(fx.nudgeAng) * fx.nudge * L.s, Math.sin(fx.nudgeAng) * fx.nudge * L.s);
    }

    // Player historical trail — quietest moving element (spec §14.1 hierarchy):
    // 75 samples (1.25 s), alpha tapering hard toward the tail so it never
    // competes with echo trails. Trail cosmetics style it (taper/dotted/pulse/duo).
    const trail = this.store.equippedItem("trail");
    const td = trail.data as Record<string, unknown>;
    const hist = engine.history;
    if (hist.count > 2) {
      const start = Math.max(hist.count - 75, Math.max(0, hist.count - CONFIG.historyCapacity));
      const count = hist.count - start;
      const baseCol = td.col as string;
      const baseW = td.width as number;
      const pulseK = td.pulse && !reduced ? 0.7 + 0.3 * Math.sin(engine.t * 5) : 1;
      ctx.save();
      ctx.lineJoin = "round";
      ctx.lineCap = "round";
      if (td.dotted) ctx.setLineDash([2 * L.s, 6 * L.s]);
      const CHUNK = 15;
      for (let c0 = start; c0 < hist.count - 1; c0 += CHUNK) {
        const cEnd = Math.min(hist.count - 1, c0 + CHUNK);
        const f = (c0 - start) / count; // 0 tail → 1 head
        ctx.beginPath();
        let first = true;
        for (let i = c0; i <= cEnd; i++) {
          const p = hist.sampleAt(i);
          if (!p) continue;
          const pp = this.A(L, p.x, p.y);
          if (first) {
            ctx.moveTo(pp[0], pp[1]);
            first = false;
          } else ctx.lineTo(pp[0], pp[1]);
        }
        ctx.globalAlpha = (0.04 + f * f * 0.85) * pulseK;
        ctx.strokeStyle = baseCol;
        ctx.lineWidth = (td.taper ? baseW * (0.3 + 0.7 * f) : baseW) * L.s;
        ctx.stroke();
        if (td.duo) {
          ctx.strokeStyle = td.duo as string;
          ctx.lineWidth = 0.7 * L.s;
          ctx.stroke();
        }
      }
      ctx.restore();
    }

    // echoes
    engine.defs.forEach((def, index) => {
      const pos = engine.echoPosition(def);
      if (!pos) return;
      const ageSteps = engine.stepIndex - def.unlockStep;
      const alpha = Math.min(ageSteps / CONFIG.spawnFadeSteps, 1);
      const col = cols[index % cols.length];

      // trail — tapered polyline, alpha fades toward tail (spec §5.5)
      ctx.save();
      const pts: [number, number][] = [];
      for (let k = 0; k < CONFIG.trailPoints; k++) {
        const tp = engine.echoTrailPoint(def, k);
        if (!tp) break;
        pts.push(this.A(L, tp.x, tp.y));
      }
      if (pts.length > 1) {
        const baseW = reduced ? 1.5 : 2.4;
        const baseA = (reduced ? 0.4 : 0.34) * alpha;
        if (contrast) ctx.setLineDash([6 - index, 3 + index]);
        for (let k = 0; k < pts.length - 1; k++) {
          const f = 1 - k / (pts.length - 1);
          ctx.beginPath();
          ctx.moveTo(pts[k][0], pts[k][1]);
          ctx.lineTo(pts[k + 1][0], pts[k + 1][1]);
          ctx.strokeStyle = col;
          ctx.globalAlpha = baseA * (0.25 + 0.75 * f);
          ctx.lineWidth = baseW * (0.35 + 0.65 * f) * L.s;
          ctx.lineJoin = "round";
          ctx.lineCap = "round";
          ctx.stroke();
        }
      }
      ctx.restore();

      const dim = fx.hitFx && fx.hitFx.age < 0.18 && fx.hitFx.echoId !== def.id ? 0.35 : 1;
      this.drawEchoHead(
        ctx,
        L,
        pos.x,
        pos.y,
        col,
        this.shapeFor(def, index),
        def.notches,
        `-${def.delay}s`,
        alpha * dim,
        reduced,
      );

      // responsible-echo ring expansion (spec §5.6)
      if (fx.hitFx && fx.hitFx.age < 0.18 && fx.hitFx.echoId === def.id) {
        const hp = this.A(L, pos.x, pos.y);
        ctx.save();
        ctx.strokeStyle = "#FF719A";
        ctx.lineWidth = 2;
        ctx.globalAlpha = 0.9;
        ctx.beginPath();
        ctx.arc(hp[0], hp[1], (14 + (0.18 - fx.hitFx.age) * 90) * L.s, 0, 6.283);
        ctx.stroke();
        ctx.restore();
      }
    });

    // telegraph countdown (spec §5.3) — first pending echo only
    for (let index = 0; index < engine.defs.length; index++) {
      const def = engine.defs[index];
      const remSteps = def.unlockStep - engine.stepIndex;
      if (remSteps > 0 && remSteps <= Math.round(CONFIG.telegraphLead * 120)) {
        const rem = remSteps / 120;
        const cp = this.A(L, 180, 240);
        ctx.save();
        ctx.textAlign = "center";
        ctx.font = `600 ${11 * L.s}px "IBM Plex Mono", monospace`;
        ctx.fillStyle = cols[index % cols.length];
        ctx.globalAlpha = reduced ? 0.85 : 0.55 + 0.45 * Math.sin(engine.t * 12);
        ctx.fillText(`NEW -${def.delay}S ECHO IN ${rem.toFixed(1)}S`, cp[0], cp[1]);
        ctx.restore();
        break;
      }
    }

    // unlock arrival — expanding ring + soft arena wash (accessibility-safe ≤8 %)
    if (fx.unlockFlash && !reduced) {
      const flash = fx.unlockFlash;
      const idx = engine.defs.findIndex((d) => d.id === flash.echoId);
      const pos = idx >= 0 ? engine.echoPosition(engine.defs[idx]) : null;
      const col = cols[Math.max(idx, 0) % cols.length];
      const k = flash.age / 0.6;
      if (pos && k < 1) {
        const p = this.A(L, pos.x, pos.y);
        ctx.save();
        ctx.strokeStyle = col;
        ctx.globalAlpha = (1 - k) * 0.7;
        ctx.lineWidth = 2 * L.s * (1 - k * 0.6);
        ctx.beginPath();
        ctx.arc(p[0], p[1], (12 + k * 120) * L.s, 0, 6.283);
        ctx.stroke();
        if (k < 0.2) {
          ctx.globalAlpha = (1 - k / 0.2) * 0.08;
          ctx.fillStyle = col;
          ctx.fillRect(0, 0, L.W, L.H);
        }
        ctx.restore();
      }
    }

    // timeline-cleared bloom — radial cyan swell from the player
    if (fx.clearAge >= 0 && fx.clearAge < 0.7 && !reduced) {
      const k = fx.clearAge / 0.7;
      const p = this.A(L, engine.px, engine.py);
      ctx.save();
      ctx.globalCompositeOperation = "lighter";
      ctx.globalAlpha = (1 - k) * 0.55;
      const g = glowSprite(core.ring);
      const GR = (30 + k * 340) * L.s;
      ctx.drawImage(g, p[0] - GR, p[1] - GR, GR * 2, GR * 2);
      ctx.restore();
    }

    // shard
    this.drawShard(ctx, L, engine, reduced);

    // pickup scale-punch ring
    if (fx.pickupPop && !reduced) {
      const k = fx.pickupPop.age / 0.3;
      const p = this.A(L, fx.pickupPop.x, fx.pickupPop.y);
      ctx.save();
      ctx.globalAlpha = (1 - k) * 0.5;
      ctx.strokeStyle = "#FFE37B";
      ctx.lineWidth = 1.5 * L.s;
      ctx.beginPath();
      ctx.arc(p[0], p[1], (12 + k * 26) * L.s, 0, 6.283);
      ctx.stroke();
      ctx.restore();
    }

    // floating score numerals — teach the combo economy in-world
    if (fx.floaters.length) {
      ctx.save();
      ctx.textAlign = "center";
      ctx.font = `600 ${11 * L.s}px "IBM Plex Mono", monospace`;
      for (const fl of fx.floaters) {
        const k = fl.age / 0.55;
        const p = this.A(L, fl.x, fl.y - k * 20);
        ctx.globalAlpha = (1 - k) * 0.9;
        ctx.fillStyle = "#FFE37B";
        ctx.fillText(fl.text, p[0], p[1] - 16 * L.s);
      }
      ctx.restore();
    }

    // particles (additive)
    if (fx.particles.length) {
      ctx.save();
      ctx.globalCompositeOperation = "lighter";
      for (const p of fx.particles) {
        const pp = this.A(L, p.x, p.y);
        ctx.globalAlpha = Math.max(p.life / p.max, 0) * 0.9;
        const g = glowSprite(p.col);
        const R = p.size * 2.2 * L.s;
        ctx.drawImage(g, pp[0] - R, pp[1] - R, R * 2, R * 2);
      }
      ctx.restore();
    }

    // hit cross + attribution message (spec §5.6)
    if (fx.hitFx) {
      const hx = this.A(L, fx.hitFx.x, fx.hitFx.y);
      ctx.save();
      ctx.strokeStyle = "#FF719A";
      ctx.lineWidth = 1.5;
      ctx.globalAlpha = 0.9;
      ctx.beginPath();
      ctx.moveTo(hx[0] - 6, hx[1] - 6);
      ctx.lineTo(hx[0] + 6, hx[1] + 6);
      ctx.moveTo(hx[0] + 6, hx[1] - 6);
      ctx.lineTo(hx[0] - 6, hx[1] + 6);
      ctx.stroke();
      ctx.textAlign = "center";
      ctx.font = `600 ${11.5 * L.s}px "IBM Plex Mono", monospace`;
      ctx.fillStyle = "#FF719A";
      const mp = this.A(L, 180, 210);
      ctx.fillText(`YOUR -${fx.hitFx.delay}S PATH CAUGHT YOU`, mp[0], mp[1]);
      ctx.restore();
    }

    // touch tether + reticle (spec §4.3); the setting makes it persistent (§16.4)
    const tetherVisible = engine.touching || fx.tetherFade > 0 || (set.tether && engine.stepIndex > 0);
    if (tetherVisible) {
      const tp = this.A(L, engine.tx, engine.ty);
      const pp = this.A(L, engine.px, engine.py);
      ctx.save();
      ctx.globalAlpha = engine.touching
        ? 0.5
        : fx.tetherFade > 0
          ? (fx.tetherFade / 0.12) * 0.5
          : 0.22; // persistent mode after release
      ctx.strokeStyle = core.ring;
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 4]);
      ctx.beginPath();
      ctx.moveTo(pp[0], pp[1]);
      ctx.lineTo(tp[0], tp[1]);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.arc(tp[0], tp[1], 7 * L.s, 0, 6.283);
      ctx.stroke();
      ctx.restore();
    }

    // player (brightest object; 7 Hz invuln blink per spec §4.5)
    const invulnBlink =
      engine.stepIndex < engine.invulnUntilStep && Math.floor(engine.t * 14) % 2 === 1;
    if (!invulnBlink) {
      const P = this.A(L, engine.px, engine.py);
      ctx.save();
      if (!reduced) {
        // double glow blit — the player wins the frame at a squint (spec §14.1)
        ctx.globalCompositeOperation = "lighter";
        const g = glowSprite(core.ring);
        const GR = 26 * L.s;
        ctx.globalAlpha = 0.75;
        ctx.drawImage(g, P[0] - GR, P[1] - GR, GR * 2, GR * 2);
        const GR2 = 14 * L.s;
        ctx.globalAlpha = 0.6;
        ctx.drawImage(g, P[0] - GR2, P[1] - GR2, GR2 * 2, GR2 * 2);
        ctx.globalCompositeOperation = "source-over";
        ctx.globalAlpha = 1;
      }
      // death chromatic ghost — static offset, no flicker (accessibility-safe)
      if (fx.deathAge >= 0 && fx.deathAge < 0.6 && !reduced) {
        ctx.globalAlpha = 0.4 * (1 - fx.deathAge / 0.6);
        ctx.strokeStyle = "#FF61D2";
        ctx.lineWidth = 2.2 * L.s;
        ctx.beginPath();
        ctx.arc(P[0] - 2.5, P[1], 10 * L.s, 0, 6.283);
        ctx.stroke();
        ctx.strokeStyle = "#42DAF6";
        ctx.beginPath();
        ctx.arc(P[0] + 2.5, P[1], 10 * L.s, 0, 6.283);
        ctx.stroke();
        ctx.globalAlpha = 1;
      }
      ctx.strokeStyle = core.ring;
      ctx.lineWidth = 2.2 * L.s;
      ctx.beginPath();
      ctx.arc(P[0], P[1], CONFIG.playerVisualRadius * L.s, 0, 6.283);
      ctx.stroke();
      ctx.fillStyle = core.core;
      ctx.beginPath();
      ctx.arc(P[0], P[1], 5.5 * L.s, 0, 6.283);
      ctx.fill();
      if (core.halo) {
        ctx.globalAlpha = 0.4;
        ctx.beginPath();
        ctx.arc(P[0], P[1], 15 * L.s, 0, 6.283);
        ctx.stroke();
        ctx.globalAlpha = 1;
      }
      if (core.orbit && !reduced) {
        const oa = engine.t * 2.6;
        ctx.fillStyle = core.ring;
        ctx.beginPath();
        ctx.arc(P[0] + Math.cos(oa) * 14 * L.s, P[1] + Math.sin(oa) * 14 * L.s, 1.6 * L.s, 0, 6.283);
        ctx.fill();
      }
      ctx.restore();
    }

    // ready pulse — MOVE (spec §9.1)
    if (readyT > 0) {
      const rp = this.A(L, 180, 260);
      const k = 1 - readyT / 0.7;
      ctx.save();
      ctx.textAlign = "center";
      ctx.font = `900 ${(30 + k * 8) * L.s}px Unbounded, sans-serif`;
      ctx.fillStyle = "#B9F8FF";
      ctx.globalAlpha = 0.25 + k * 0.75;
      ctx.fillText("MOVE", rp[0], rp[1]);
      ctx.restore();
    }

    if (trainingBanner) {
      const tb = this.A(L, 180, 18);
      ctx.save();
      ctx.textAlign = "center";
      ctx.font = `600 ${9 * L.s}px "IBM Plex Mono", monospace`;
      ctx.fillStyle = "#FFE37B";
      ctx.globalAlpha = 0.85;
      ctx.fillText("· TRAINING RUN — UNRANKED ·", tb[0], tb[1]);
      ctx.restore();
    }

    ctx.restore();
  }

  drawEchoHead(
    ctx: CanvasRenderingContext2D,
    L: Layout,
    x: number,
    y: number,
    col: string,
    shape: string,
    notches: number,
    label: string,
    alpha: number,
    reduced: boolean,
    showLabel = true,
  ): void {
    const P = this.A(L, x, y);
    const R = CONFIG.echoVisualRadius * L.s;
    ctx.save();
    ctx.globalAlpha = alpha;
    if (!reduced) {
      ctx.save();
      ctx.globalCompositeOperation = "lighter";
      ctx.globalAlpha = alpha * 0.28; // player must out-glow echoes (spec §14.1)
      const g = glowSprite(col);
      const GR = R * 2.1;
      ctx.drawImage(g, P[0] - GR, P[1] - GR, GR * 2, GR * 2);
      ctx.restore();
    }
    ctx.strokeStyle = col;
    ctx.fillStyle = col;
    ctx.lineWidth = 2 * L.s;
    drawShapePath(ctx, P[0], P[1], R, shape);
    // center dot
    ctx.beginPath();
    ctx.arc(P[0], P[1], 3 * L.s, 0, 6.283);
    ctx.fill();
    // identity notches (color-independent identity, spec §2C)
    for (let n = 0; n < notches; n++) {
      const na = -Math.PI / 2 + n * 0.55 - (notches - 1) * 0.275;
      ctx.beginPath();
      ctx.moveTo(P[0] + Math.cos(na) * (R + 2 * L.s), P[1] + Math.sin(na) * (R + 2 * L.s));
      ctx.lineTo(P[0] + Math.cos(na) * (R + 5 * L.s), P[1] + Math.sin(na) * (R + 5 * L.s));
      ctx.lineWidth = 1.5 * L.s;
      ctx.stroke();
    }
    if (showLabel) {
      // R+13 keeps the label clear of the notch fan (which ends at R+5)
      ctx.textAlign = "center";
      ctx.font = `600 ${8.5 * L.s}px "IBM Plex Mono", monospace`;
      ctx.fillStyle = col;
      ctx.fillText(label, P[0], P[1] - R - 13 * L.s);
    }
    ctx.restore();
  }

  private drawShard(ctx: CanvasRenderingContext2D, L: Layout, engine: EchoEngine, reduced: boolean): void {
    const shardItem = this.store.equippedItem("shard");
    const sd = shardItem.data as Record<string, unknown>;
    const P = this.A(L, engine.shard.x, engine.shard.y);
    // presentation clock, not sim time — the shard breathes during the ready
    // pulse and pause instead of freezing (1.8 rad/s per spec §14.3)
    const t = this.ambientT;
    const rot = t * 1.8;
    const breathe = reduced ? 1 : 1 + 0.05 * Math.sin(t * 3);
    const points = (sd.points as number) ?? 4;
    const fill = (sd.fill as string) ?? "#FFE37B";
    if (!reduced) {
      ctx.save();
      ctx.globalCompositeOperation = "lighter";
      ctx.globalAlpha = 0.5;
      const g = glowSprite("#FFE37B");
      const GR = 22 * L.s;
      ctx.drawImage(g, P[0] - GR, P[1] - GR, GR * 2, GR * 2);
      ctx.restore();
    }
    ctx.save();
    ctx.translate(P[0], P[1]);
    ctx.rotate(rot);
    ctx.scale(breathe, breathe);
    ctx.fillStyle = fill;
    ctx.beginPath();
    const R1 = 12 * L.s;
    const R2 = 4.2 * L.s;
    for (let i = 0; i < points * 2; i++) {
      const r = i % 2 === 0 ? R1 : R2;
      const a = (i * Math.PI) / points;
      const x = Math.cos(a) * r;
      const y = Math.sin(a) * r;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = "#FFFDF2";
    ctx.beginPath();
    ctx.arc(0, 0, 2.6 * L.s, 0, 6.283);
    ctx.fill();
    ctx.restore();
  }

  // ---------- HUD ----------

  drawHUD(engine: EchoEngine, L: Layout, fx?: RenderFx): void {
    const ctx = this.canvas.getContext("2d")!;
    const W = L.W;
    const infinite = engine.infinite;
    const best =
      engine.kind === "daily"
        ? (this.store.data.bestDaily[new Date().toISOString().slice(0, 10)] ?? 0)
        : this.store.data.bestClassic;
    ctx.save();
    ctx.textBaseline = "alphabetic";
    const canTrack = "letterSpacing" in ctx;
    const microLabel = (text: string, x: number, y: number, align: CanvasTextAlign): void => {
      ctx.save();
      if (canTrack) (ctx as CanvasRenderingContext2D & { letterSpacing: string }).letterSpacing = "1.4px";
      ctx.textAlign = align;
      ctx.font = '600 9px "IBM Plex Mono", monospace';
      ctx.fillStyle = "#A7ABC0";
      ctx.fillText(text, x, y);
      ctx.restore();
    };
    // top row — one shared value baseline at ty+22
    const ty = 84;
    const lx = 18;
    ctx.textAlign = "left";
    microLabel("SCORE", lx, ty, "left");
    ctx.font = '600 21px "IBM Plex Mono", monospace';
    ctx.fillStyle = "#F2F5FF";
    ctx.fillText(engine.kind === "practice" ? "—" : engine.score.toLocaleString(), lx, ty + 22);
    microLabel(engine.kind === "daily" ? "TODAY" : "BEST", lx + 92, ty, "left");
    ctx.font = '600 14px "IBM Plex Mono", monospace';
    ctx.fillStyle = "#7C8199";
    ctx.fillText(best > 0 ? best.toLocaleString() : "—", lx + 92, ty + 22);

    if (engine.combo > 1) {
      const freshSteps = Math.max(0, 1 - (engine.stepIndex - engine.lastPickStep) / CONFIG.comboWindowSteps);
      const pillX = W / 2 - 26;
      ctx.fillStyle = `rgba(255,227,123,${0.14 + freshSteps * 0.1})`;
      roundRect(ctx, pillX, ty - 10, 52, 30, 15);
      ctx.fill();
      ctx.strokeStyle = "rgba(255,227,123,.5)";
      ctx.lineWidth = 1;
      roundRect(ctx, pillX, ty - 10, 52, 30, 15);
      ctx.stroke();
      ctx.textAlign = "center";
      ctx.font = '600 14px "IBM Plex Mono", monospace';
      ctx.fillStyle = "#FFE37B";
      ctx.fillText(`x${engine.combo}`, pillX + 26, ty + 8);
      // decay meter lives inside the capsule
      ctx.strokeStyle = "rgba(255,227,123,.9)";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(pillX + 10, ty + 14);
      ctx.lineTo(pillX + 10 + 32 * freshSteps, ty + 14);
      ctx.stroke();
      ctx.textAlign = "left";
    }

    const remSeconds = infinite ? null : Math.max(0, (CONFIG.roundSteps - engine.stepIndex) / 120);
    microLabel("TIME", W - 70, ty, "right");
    ctx.textAlign = "right";
    ctx.font = '600 21px "IBM Plex Mono", monospace';
    ctx.fillStyle = remSeconds !== null && remSeconds <= 6 ? "#FF719A" : "#F2F5FF";
    ctx.fillText(
      remSeconds === null ? "∞" : remSeconds < 10 ? remSeconds.toFixed(1) : String(Math.ceil(remSeconds)),
      W - 70,
      ty + 22,
    );

    // bottom row — measured legend widths, constant 22 px gaps
    const by = L.H - 58;
    const cols = this.paletteCols();
    let ex = 18;
    ctx.textAlign = "left";
    ctx.font = '600 8.5px "IBM Plex Mono", monospace';
    engine.defs.forEach((def, i) => {
      const active = engine.echoActive(def);
      const remSteps = def.unlockStep - engine.stepIndex;
      const soon = !active && remSteps <= Math.round(CONFIG.telegraphLead * 120);
      const text = `-${def.delay}s`;
      const textW = ctx.measureText(text).width;
      ctx.save();
      ctx.globalAlpha = active ? 1 : soon ? 0.4 + 0.6 * Math.abs(Math.sin(engine.t * 6)) : 0.28;
      drawMiniShape(ctx, ex + 8, by, 7, cols[i % cols.length], this.shapeFor(def, i));
      ctx.font = '600 8.5px "IBM Plex Mono", monospace';
      ctx.fillStyle = active || soon ? cols[i % cols.length] : "#5B6078";
      ctx.fillText(text, ex + 20, by + 3.2);
      ctx.restore();
      ex += 20 + textW + 22;
    });

    for (let lp = 0; lp < 3; lp++) {
      const px = W - 26 - lp * 20;
      ctx.beginPath();
      ctx.arc(px, by, 5.5, 0, 6.283);
      if (lp < engine.lives) {
        ctx.fillStyle = "#42DAF6";
        ctx.fill();
      } else {
        ctx.strokeStyle = "rgba(167,171,192,.35)";
        ctx.lineWidth = 1.3;
        ctx.stroke();
      }
    }
    // life-loss flash — expanding coral ring off the emptied pip
    if (fx?.lifeFlash) {
      const k = fx.lifeFlash.age / 0.45;
      const px = W - 26 - fx.lifeFlash.index * 20;
      ctx.save();
      ctx.globalAlpha = (1 - k) * 0.9;
      ctx.strokeStyle = "#FF719A";
      ctx.lineWidth = 1.6;
      ctx.beginPath();
      ctx.arc(px, by, 5.5 + k * 14, 0, 6.283);
      ctx.stroke();
      ctx.restore();
    }

    if (!infinite && remSeconds !== null) {
      const bw = W - 36;
      const frac = Math.max(0, remSeconds / 30);
      const urgent = remSeconds <= 6;
      // final-6-seconds emphasis: the bar thickens with a per-second pulse
      const barH = urgent ? 5 + Math.max(0, Math.sin(engine.t * Math.PI * 2)) * 1.5 : 4;
      ctx.fillStyle = "rgba(167,171,192,.12)";
      roundRect(ctx, 18, by + 16, bw, barH, barH / 2);
      ctx.fill();
      ctx.fillStyle = urgent ? "#FF719A" : "#FFE37B";
      if (frac > 0.003) {
        roundRect(ctx, 18, by + 16, bw * frac, barH, barH / 2);
        ctx.fill();
      }
    }
    ctx.restore();
  }

  clearCanvas(): void {
    const L = this.layout();
    if (!L) return;
    const ctx = this.canvas.getContext("2d")!;
    this.drawBackground(ctx, L);
  }
}

export function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function drawShapePath(ctx: CanvasRenderingContext2D, x: number, y: number, R: number, shape: string): void {
  switch (shape) {
    case "diamond":
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(Math.PI / 4);
      ctx.strokeRect(-R * 0.78, -R * 0.78, R * 1.56, R * 1.56);
      ctx.restore();
      break;
    case "split":
      ctx.beginPath();
      ctx.arc(x, y, R, -1.35, 1.35);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(x, y, R, 1.8, 4.5);
      ctx.stroke();
      break;
    case "hex":
      polygon(ctx, x, y, R, 6, -Math.PI / 6);
      break;
    case "ringdot": {
      ctx.beginPath();
      ctx.arc(x, y, R, 0, 6.283);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(x, y - R, 2, 0, 6.283);
      ctx.fill();
      break;
    }
    case "triangle":
      polygon(ctx, x, y, R * 1.1, 3, -Math.PI / 2);
      break;
    case "square":
      ctx.strokeRect(x - R * 0.8, y - R * 0.8, R * 1.6, R * 1.6);
      break;
    case "octagon":
      polygon(ctx, x, y, R, 8, Math.PI / 8);
      break;
    default:
      ctx.beginPath();
      ctx.arc(x, y, R, 0, 6.283);
      ctx.stroke();
  }
}

function polygon(ctx: CanvasRenderingContext2D, x: number, y: number, R: number, n: number, rot: number): void {
  ctx.beginPath();
  for (let i = 0; i < n; i++) {
    const a = (i * 2 * Math.PI) / n + rot;
    const px = x + Math.cos(a) * R;
    const py = y + Math.sin(a) * R;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
  ctx.stroke();
}

export function drawMiniShape(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  col: string,
  shape: string,
): void {
  ctx.strokeStyle = col;
  ctx.fillStyle = col;
  ctx.lineWidth = 1.6;
  drawShapePath(ctx, x, y, r, shape);
}
